import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import LoginForm from "@/modules/auth/login/ui/components/LoginForm"
import { LoginAd } from "../components/LoginAd"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { AppRole } from "@/modules/auth/constants/app-role"
import { AuthErrorPayload } from "@/modules/auth/entities/AuthError"
import { useRecaptcha } from "@/modules/shared/hooks/use-recaptcha"
import { AxiosError } from "axios"
import { HttpStatusCodes } from "@/lib/http-status-codes"

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null)
  const navigate = useNavigate()
  const { loginWithCredentials } = useAuth()
  const { getToken } = useRecaptcha()

  useEffect(() => {
    if (!cooldownUntil) return
    const tick = () => {
      if (Date.now() >= cooldownUntil) {
        setCooldownUntil(null)
      }
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [cooldownUntil])

  const isCoolingDown = cooldownUntil !== null && Date.now() < cooldownUntil

  const handleLocalLogin = async (userName: string, password: string) => {
    setIsLoading(true)
    try {
      const captchaToken = await getToken("login")
      const result = await loginWithCredentials(userName, password, undefined, captchaToken)
      if (result?.user?.user?.needsLegalConsent) {
        navigate("/accept-legal-documents", { replace: true })
        return
      }
      const isEmployee = result?.user?.roles?.includes(AppRole.Employee)
      navigate(isEmployee ? "/employee/dashboard" : "/dashboard", { replace: true })
    } catch (err: unknown) {
      const axiosErr = err as AxiosError
      if (axiosErr?.response?.status === HttpStatusCodes.TOO_MANY_REQUESTS) {
        const retryAfter = parseInt(
          (axiosErr.response.headers as Record<string, string>)?.['retry-after'] ?? '60',
          10
        )
        setCooldownUntil(Date.now() + retryAfter * 1000)
        return
      }
      const authErr = err as AuthErrorPayload
      if (authErr.errorCode === "VERIFICATION_FAILED") {
        sessionStorage.setItem("signupEmail", userName)
        navigate("/otp")
        return
      }
      throw authErr
    } finally {
      setIsLoading(false)
    }
  }

  const handleMicrosoftLogin = () => {
    console.warn("Microsoft login is not implemented yet.")
  }

  return (
    <div className="force-light bg-background text-foreground flex flex-col md:flex-row w-full min-h-screen">
      <LoginAd />
      <div className="flex-1 flex items-center justify-start">
        <div className="w-full max-w-full pr-4 md:pl-30 max-md:pl-4">
          <LoginForm
            onSubmit={handleLocalLogin}
            onMicrosoftLogin={handleMicrosoftLogin}
            isLoading={isLoading}
            isCoolingDown={isCoolingDown}
          />
        </div>
      </div>
    </div>
  )
}
